// /var/www/oszczedzam/client/src/hooks/useTransactions.js
import { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, push, get, update, remove, query, orderByChild } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';

export function useTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  // Pobierz wszystkie transakcje
  const fetchTransactions = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const transactionsRef = ref(database, `users/${currentUser.uid}/transactions`);
      const snapshot = await get(transactionsRef);
      
      const transactionsData = [];
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          transactionsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
      }
      
      setTransactions(transactionsData);
      setError(null);
    } catch (err) {
      console.error("Błąd podczas pobierania transakcji:", err);
      setError('Nie udało się pobrać transakcji.');
    } finally {
      setLoading(false);
    }
  };

  // Dodaj nową transakcję
  const addTransaction = async (transactionData) => {
    if (!currentUser) return null;
    
    try {
      const transactionsRef = ref(database, `users/${currentUser.uid}/transactions`);
      const newTransactionRef = await push(transactionsRef, {
        ...transactionData,
        createdAt: new Date().toISOString()
      });
      
      const newTransaction = {
        id: newTransactionRef.key,
        ...transactionData,
        createdAt: new Date().toISOString()
      };
      
      setTransactions([...transactions, newTransaction]);
      return newTransaction;
    } catch (err) {
      console.error("Błąd podczas dodawania transakcji:", err);
      throw err;
    }
  };

  // Zaktualizuj transakcję
  const updateTransaction = async (transactionId, transactionData) => {
    if (!currentUser) return null;
    
    try {
      const transactionRef = ref(database, `users/${currentUser.uid}/transactions/${transactionId}`);
      await update(transactionRef, {
        ...transactionData,
        updatedAt: new Date().toISOString()
      });
      
      const updatedTransactions = transactions.map(t => 
        t.id === transactionId 
          ? { ...t, ...transactionData, updatedAt: new Date().toISOString() } 
          : t
      );
      
      setTransactions(updatedTransactions);
      return { id: transactionId, ...transactionData };
    } catch (err) {
      console.error("Błąd podczas aktualizacji transakcji:", err);
      throw err;
    }
  };

  // Usuń transakcję
  const deleteTransaction = async (transactionId) => {
    if (!currentUser) return false;
    
    try {
      const transactionRef = ref(database, `users/${currentUser.uid}/transactions/${transactionId}`);
      await remove(transactionRef);
      
      setTransactions(transactions.filter(t => t.id !== transactionId));
      return true;
    } catch (err) {
      console.error("Błąd podczas usuwania transakcji:", err);
      throw err;
    }
  };

  // Pobierz transakcje przy pierwszym renderowaniu
  useEffect(() => {
    fetchTransactions();
  }, [currentUser]);

  return {
    transactions,
    loading,
    error,
    fetchTransactions,
    addTransaction,
    updateTransaction,
    deleteTransaction
  };
}